import clsx from "clsx"
import { createElement } from "react"
import { type FieldMetaState, type FieldRenderProps, useField } from "react-final-form"

import { DatePickerRevamped, type DatePickerRevampedProps } from "./date-picker-revamped"
import type { InputProps } from "./input"
import { Input } from "./input"
import type { SelectProps } from "./select"
import { Select } from "./select"
import type { TagInputProps } from "./tag-input"
import { TagInput } from "./tag-input"
import type { TextareaProps } from "./textarea"
import { Textarea } from "./textarea"
import type { ToggleProps } from "./toggle"
import { Toggle } from "./toggle"

type FieldErrorProps = {
	name: string
	className?: string
	withFieldName?: boolean
}

type FieldComponentProps<V, T extends HTMLElement = HTMLElement> = {
	input: FieldRenderProps<V, T>["input"]
	// should be types to FieldMetaState<V>, but this cause conflitcs when Field.parse and Field.format from final-form are used, because types can not be inferred there from the parent Field
	meta: Pick<FieldMetaState<V>, "touched" | "invalid" | "submitError" | "error" | "dirty">
}

export function FieldError({ name, className, withFieldName = false }: FieldErrorProps) {
	const { meta } = useField(name, {
		subscription: {
			error: true,
			submitError: true,
			dirty: true,
			touched: true,
		},
	})

	const error = meta.submitError || meta.error
	if ((meta.touched || meta.dirty) && error)
		return (
			<div className={clsx(className, "text-sm text-red-700")}>
				{withFieldName ? `${name}: ` : undefined}
				{error}
			</div>
		)
	return null
}

const extractProps = <V, T extends HTMLElement = HTMLElement>({ input, meta, ...rest }: FieldComponentProps<V, T>) => ({
	...input,
	...rest,
	invalid: meta.touched && meta.invalid,
})
export function FormToggle(props: ToggleProps & FieldComponentProps<boolean | string, HTMLInputElement>) {
	return createElement(Toggle, extractProps<boolean | string, HTMLInputElement>(props) as unknown as typeof props)
}

export function FormDatePickerRevamped<R extends Date | string | null = string>(
	props: DatePickerRevampedProps<R> & FieldComponentProps<R>,
) {
	return createElement(DatePickerRevamped, extractProps(props))
}

export function FormInput<V extends string | number | null | undefined = string>(
	props: InputProps & FieldComponentProps<V, HTMLInputElement>,
) {
	return createElement(Input, extractProps(props) as unknown as typeof props)
}
export function FormTagInput(props: TagInputProps & FieldComponentProps<string[]>) {
	return createElement(TagInput, extractProps(props))
}

export function FormTextarea<V extends string | null | undefined = string>(
	props: TextareaProps & FieldComponentProps<V, HTMLTextAreaElement>,
) {
	const {
		input: { onFocus: _onFocus, onBlur: _onBlur, ...restInput },
		meta,
		...rest
	} = props
	return <Textarea isInvalid={meta.touched && meta.invalid} {...restInput} {...rest} />
}

export function FormSelect(props: SelectProps<unknown> & FieldComponentProps<unknown>) {
	return createElement(Select, extractProps(props))
}
